import React, { Fragment, useState, useEffect } from 'react';
import { Col, Card, CardHeader, CardBody, Form, FormGroup, Label, Row, Input } from 'reactstrap';
import Select from 'react-select';
import { useForm, Controller } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useSearchParams, NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Btn, H3, Breadcrumbs, H4 } from '../../../AbstractElements';
import { getClientUrlDataToken, postClientUrlWithToken } from '../../../Utils/restUtils';
import { getUsers, getUsersRole, addAgentCollection, addAgentCollectionRole, getAgentCollectionList, getAgentCollectionListRole } from '../../../CommonUrl';
import Transparentspinner from '../../Common/Loader/Transparentspinner';
import { Save, Cancel, Notes } from '../../../Constant';

const schema = Yup.object().shape({
    agent: Yup.object().required('Agent is required').nullable(),
    amount: Yup.number()
        .typeError('Amount must be a number')
        .required('Amount is required')
        .positive('Amount must be positive')
        .max(9999999, 'Amount is too large'),
    notes: Yup.string().nullable()
});

const AddCollectionAmount = () => {
    const { handleSubmit, control, setValue, watch, setError, clearErrors, formState: { errors } } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            agent: null,
            amount: '',
            notes: ''
        }
    });

    const authState = useSelector((state) => state.auth);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const agentIdParam = searchParams.get('id');
    const [agentOptions, setAgentOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [agentDetails, setAgentDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);

    const agentValue = watch('agent');

    const handleApiError = (status) => {
        if (status >= 400 && status <= 405) {
            navigate(`${process.env.PUBLIC_URL}/pages/error/error-page1`);
        } else if (status >= 500 && status <= 505) {
            navigate(`${process.env.PUBLIC_URL}/pages/error/error-page4`);
        } else {
            navigate(`${process.env.PUBLIC_URL}/pages/error/error-page3`);
        }
    };

    const fetchAgents = async () => {
        setLoading(true);
        try {
            const data = await getClientUrlDataToken(
                authState.apiUrl,
                authState.userRole === 1 ? getUsers : getUsersRole
            );
            if (data.status === true) {
                const options = data.data?.map((item) => ({
                    label: item?.user_name,
                    value: item?.user_id,
                })) || [];
                setAgentOptions(options);

                if (agentIdParam) {
                    const preselected = options.find(opt => String(opt.value) === String(agentIdParam));
                    if (preselected) {
                        setValue('agent', preselected);
                    }
                }
            } else {
                handleApiError(data.status);
            }
        } catch (err) {
            console.error('Error fetching agents:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchAgentDetails = async (agentId) => {
        if (!agentId) {
            setAgentDetails(null);
            return;
        }
        setDetailsLoading(true);
        try {
            const baseUrl = authState.userRole === 1 ? getAgentCollectionList : getAgentCollectionListRole;
            const response = await getClientUrlDataToken(
                authState.apiUrl,
                `${baseUrl}?agent_id=${agentId}`
            );
            if (response.status === true && response.data && Array.isArray(response.data.data)) {
                // Find matching agent or fall back to first item in case list is filtered
                const details = response.data.data.find(e => String(e.id) === String(agentId)) || response.data.data[0];
                if (details) {
                    setAgentDetails({
                        total_collected_amount: details.total_collected_amount || 0,
                        total_received_amount: details.total_received_amount || 0,
                        balance_amount: details.balance_amount || 0
                    });
                } else {
                    setAgentDetails(null);
                }
            } else {
                setAgentDetails(null);
            }
        } catch (err) {
            console.error('Error fetching agent details:', err);
            setAgentDetails(null);
        } finally {
            setDetailsLoading(false);
        }
    };

    useEffect(() => {
        fetchAgents();
    }, [agentIdParam]);

    useEffect(() => {
        if (agentValue && agentValue.value) {
            fetchAgentDetails(agentValue.value);
        } else {
            setAgentDetails(null);
        }
        setValue('amount', '');
        clearErrors('amount');
    }, [agentValue]);

    const validateAmountAgainstBalance = (value) => {
        if (agentDetails && value !== '' && value !== undefined && value !== null) {
            const enteredAmount = parseFloat(value);
            const balanceAmount = parseFloat(agentDetails.balance_amount);
            if (!isNaN(enteredAmount) && !isNaN(balanceAmount) && enteredAmount > balanceAmount) {
                setError('amount', {
                    type: 'manual',
                    message: `Received amount (₹${enteredAmount}) cannot exceed the current staff balance (₹${balanceAmount}).`
                });
                return false;
            }
        }
        clearErrors('amount');
        return true;
    };

    const onSubmit = async (data) => {
        if (!validateAmountAgainstBalance(data.amount)) return;
        setLoading(true);
        const postRequest = {
            agent_id: data.agent.value,
            amount: data.amount,
            notes: data.notes || '',
        };

        try {
            const response = await postClientUrlWithToken(
                authState.apiUrl,
                authState.userRole === 1 ? addAgentCollection : addAgentCollectionRole,
                postRequest
            );

            if (response.status === 200) {
                if (response.data.status === true) {
                    toast.success(response.data.message || 'Payment received successfully');
                    navigate(`${process.env.PUBLIC_URL}/billing/agent-payment-list`);
                } else {
                    toast.error(response.data.message || 'Failed to receive payment');
                }
            } else {
                handleApiError(response.status);
            }
        } catch (err) {
            console.error('Error submitting agent payment:', err);
            toast.error('An error occurred while submitting payment.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Fragment>
            {(loading || detailsLoading) && <Transparentspinner />}
            <Breadcrumbs mainTitle="Collect Agent Payment" parent="Billing" title="Collect Agent Payment" />
            <Card>
                <CardHeader>
                    <H3>Collect Agent Payment</H3>
                </CardHeader>
                <CardBody>
                    <Form className="needs-validation" noValidate onSubmit={handleSubmit(onSubmit)}>
                        <Row className="g-3 pt-3">
                            <Col md="6">
                                <Label className="form-label" for="agent">
                                    Select Agent <span className="requireStar">*</span>
                                </Label>
                                <Controller
                                    name="agent"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            options={agentOptions}
                                            placeholder="Choose an agent..."
                                            isClearable
                                            styles={{
                                                control: (baseStyles) => ({
                                                    ...baseStyles,
                                                    height: 45,
                                                }),
                                                menu: (baseStyles) => ({
                                                    ...baseStyles,
                                                    zIndex: 1000,
                                                }),
                                            }}
                                            theme={(theme) => ({
                                                ...theme,
                                                colors: {
                                                    ...theme.colors,
                                                    primary25: 'rgba(83, 70, 134, 0.474)',
                                                    primary: '#534686',
                                                    primary50: 'rgba(83, 70, 134, 0.3)'
                                                },
                                            })}
                                        />
                                    )}
                                />
                                {errors.agent && <span className="text-danger">{errors.agent.message}</span>}
                            </Col>

                            <Col md="6">
                                <Label className="form-label" for="amount">
                                    Amount <span className="requireStar">*</span>
                                </Label>
                                <Controller
                                    name="amount"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            {...field}
                                            className="form-control"
                                            id="amount"
                                            type="number"
                                            placeholder="Enter amount"
                                            style={{ height: 45 }}
                                            onChange={(e) => {
                                                field.onChange(e);
                                                validateAmountAgainstBalance(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                {errors.amount && <span className="text-danger">{errors.amount.message}</span>}
                            </Col>

                            {agentDetails && (
                                <Col md="12" className="mt-4">
                                    <div className="p-4 rounded border" style={{ backgroundColor: '#f9f9fb', borderColor: '#e9e9f2' }}>
                                        <H4 className="mb-3 text-dark">Amount Details</H4>
                                        <Row className="g-3">
                                            <Col sm="4">
                                                <div className="p-3 bg-white border rounded text-center shadow-sm">
                                                    <span className="text-muted d-block mb-1" style={{ fontSize: '13px', fontWeight: '500' }}>Total Collected Amount</span>
                                                    <H3 style={{ color: '#534686', fontWeight: '700', margin: 0 }}>{agentDetails.total_collected_amount}</H3>
                                                </div>
                                            </Col>
                                            <Col sm="4">
                                                <div className="p-3 bg-white border rounded text-center shadow-sm">
                                                    <span className="text-muted d-block mb-1" style={{ fontSize: '13px', fontWeight: '500' }}>Total Received Amount</span>
                                                    <H3 style={{ color: '#10b981', fontWeight: '700', margin: 0 }}>{agentDetails.total_received_amount}</H3>
                                                </div>
                                            </Col>
                                            <Col sm="4">
                                                <div className="p-3 bg-white border rounded text-center shadow-sm">
                                                    <span className="text-muted d-block mb-1" style={{ fontSize: '13px', fontWeight: '500' }}>Balance Amount</span>
                                                    <H3 style={{ color: agentDetails.balance_amount > 0 ? '#ef4444' : '#1e293b', fontWeight: '700', margin: 0 }}>
                                                        {agentDetails.balance_amount}
                                                    </H3>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                </Col>
                            )}

                            <Col md="12" className="mt-3">
                                <Label className="form-label" for="notes">
                                    {Notes}
                                </Label>
                                <Controller
                                    name="notes"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            {...field}
                                            type="textarea"
                                            className="form-control"
                                            id="notes"
                                            placeholder="Enter notes..."
                                            rows="4"
                                        />
                                    )}
                                />
                                {errors.notes && <span className="text-danger">{errors.notes.message}</span>}
                            </Col>
                        </Row>

                        <FormGroup className="mt-4">
                            <Btn attrBtn={{ color: "primary" }} type="submit"> {Save} </Btn>
                            <NavLink className="btn btn-outline-primary mx-2" to={`${process.env.PUBLIC_URL}/billing/agent-payment-list`}> {Cancel} </NavLink>
                        </FormGroup>
                    </Form>
                </CardBody>
            </Card>
        </Fragment>
    );
};

export default AddCollectionAmount;